import { apiUrl } from "@/lib/api";
import { authStore } from "@/lib/auth-store";

// The client for the Go change stream (ADR-0007 slice E).
//
// This replaces `pb.collection(...).subscribe(...)`, which connected to
// PocketBase's realtime API and let a collection's view rule decide what the
// client was allowed to receive. Here the server authorizes a TOPIC once and
// re-checks it while the stream is open, so the last read path whose access
// control lived in a filter-DSL string moves into Go with the rest.
//
// It reads the stream with fetch + ReadableStream rather than EventSource.
// EventSource cannot send headers, which would have meant putting the auth
// token in the query string — where it lands in proxy and gateway access logs.
// The cost is parsing the SSE framing and owning reconnection here, and
// reconnection was needed anyway: EventSource's own retry cannot tell the
// caller it missed messages, which is the one thing the caller must know.
//
// See apps/pocketbase/stream/.
export namespace StreamApi {
  export type Change = {
    topic: string;
    /** The `_col` id, so one mission topic can feed several pieces of state. */
    collection: string;
    action: "create" | "update" | "delete";
    record: Record<string, unknown>;
  };

  export function missionTopic(missionId: string): string {
    return `mission:${missionId}`;
  }
  export function mapValuesTopic(missionId: string): string {
    return `map-values:${missionId}`;
  }
  export function featureFlagTopic(flagId: string): string {
    return `feature-flag:${flagId}`;
  }
  export function siteTopic(siteId: string): string {
    return `site:${siteId}`;
  }
  export const VESSEL_POSITIONS_TOPIC = "vessel-positions";

  export type SubscribeOptions = {
    /** Called for every change on the subscribed topics. */
    onChange: (change: Change) => void;
    /**
     * Called when messages were missed — the server dropped us for lagging, or
     * the connection broke and came back. The caller must refetch: its state
     * has holes and is no longer live.
     */
    onResync?: () => void;
  };

  // A report left open overnight will see the backend restart for a release; it
  // should come back without hammering it.
  const RECONNECT_BASE_MS = 1_000;
  const RECONNECT_MAX_MS = 30_000;

  // ── one multiplexed connection ─────────────────────────────────────────────
  //
  // Every subscriber shares a single connection carrying the union of their
  // topics. The first version opened one per subscribe() call, which a report
  // page turned into six concurrent long-lived requests — and browsers cap
  // HTTP/1.1 at six per origin, so the seventh request (the submit POST, the
  // next page's data) queued behind them forever. PocketBase's own realtime
  // multiplexes for exactly this reason.

  type Subscription = { topics: string[]; opts: SubscribeOptions; connectedOnce: boolean };

  const subscriptions = new Set<Subscription>();
  let connection: AbortController | null = null;
  let connectedTopics = "";
  let syncScheduled = false;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  function desiredTopics(): string[] {
    const all = new Set<string>();
    for (const sub of subscriptions) for (const t of sub.topics) all.add(t);
    return [...all].sort();
  }

  /**
   * Reconcile the connection with the current topic set, on a macrotask so a
   * page mounting six hooks in one commit opens one connection rather than
   * rebuilding it six times.
   */
  function scheduleSync(): void {
    if (syncScheduled) return;
    syncScheduled = true;
    setTimeout(() => {
      syncScheduled = false;
      void syncConnection();
    }, 0);
  }

  async function syncConnection(): Promise<void> {
    const topics = desiredTopics();
    const key = topics.join(",");

    if (topics.length === 0) {
      connection?.abort();
      connection = null;
      connectedTopics = "";
      return;
    }
    if (connection && key === connectedTopics) return;

    connection?.abort();
    connectedTopics = key;
    const controller = new AbortController();
    connection = controller;

    while (!controller.signal.aborted) {
      try {
        // The base URL comes from api.ts rather than the Config module
        // directly; tests mock "@/lib/api", so config.ts's top-level await on
        // fetch("/config.json") never runs in them.
        const url = new URL(apiUrl("/app/stream"));
        for (const topic of topics) url.searchParams.append("topics", topic);

        const response = await fetch(url.toString(), {
          headers: { Authorization: authStore.token },
          signal: controller.signal,
        });

        if (response.status === 403) {
          // Refused: reconnecting would only be refused again.
          if (connection === controller) {
            connection = null;
            connectedTopics = "";
          }
          return;
        }
        if (!response.ok || !response.body) throw new Error(`stream: ${response.status}`);

        attempt = 0;
        // A subscriber that was already receiving has now missed whatever
        // happened across the gap; a brand-new one has not.
        for (const sub of subscriptions) {
          if (sub.connectedOnce) sub.opts.onResync?.();
          sub.connectedOnce = true;
        }

        await readEvents(response.body, controller);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.debug("stream: connection lost, retrying", error);
      }

      if (controller.signal.aborted) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
      attempt += 1;
      await new Promise<void>((resolve) => {
        reconnectTimer = setTimeout(resolve, delay);
      });
    }
  }

  function deliver(change: Change): void {
    for (const sub of subscriptions) {
      if (sub.topics.includes(change.topic)) sub.opts.onChange(change);
    }
  }

  function notifyLagged(): void {
    for (const sub of subscriptions) sub.opts.onResync?.();
  }

  /** Subscribe to one or more topics. Returns an unsubscribe function. */
  export function subscribe(topics: string[], opts: SubscribeOptions): () => void {
    if (topics.length === 0) return () => {};

    const sub: Subscription = { topics: [...topics], opts, connectedOnce: false };
    subscriptions.add(sub);
    scheduleSync();

    return () => {
      subscriptions.delete(sub);
      scheduleSync();
    };
  }

  /** Test seam: drop every subscriber and close the connection. */
  export function reset(): void {
    subscriptions.clear();
    connection?.abort();
    connection = null;
    connectedTopics = "";
    attempt = 0;
    if (reconnectTimer) clearTimeout(reconnectTimer);
  }

  /**
   * Parse the SSE framing off the body: events are separated by a blank line,
   * and a line starting with ":" is a keepalive comment.
   */
  async function readEvents(body: ReadableStream<Uint8Array>, controller: AbortController): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });

      let split = buffer.indexOf("\n\n");
      while (split !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        handleFrame(frame, controller);
        split = buffer.indexOf("\n\n");
      }
    }
  }

  function handleFrame(frame: string, controller: AbortController): void {
    let event = "message";
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith(":")) continue; // keepalive
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    }

    if (event === "lagged") {
      // Our state has holes in it; refetch rather than carry on looking live.
      notifyLagged();
      return;
    }
    if (event === "unauthorized") {
      controller.abort();
      return;
    }
    if (event !== "change" || dataLines.length === 0) return;

    try {
      deliver(JSON.parse(dataLines.join("\n")) as Change);
    } catch (error) {
      console.error("stream: unreadable change payload", error);
    }
  }
}
